"use client";
import Image, { StaticImageData } from "next/image";
import React, { useState, FC, useCallback, useMemo } from "react";
import videoPlay from "@/assets/images/videos/video-play.svg";
import { videosData } from "./VideosData";

interface Video {
  id: number;
  videoid: string;
  tabId?: number;
  thumb: StaticImageData | string;
  alt?: string;
  title: string;
  description: string;
}

interface ModalProps {
  isOpen: boolean;
  toggleModal: () => void;
  videoid: string | null;
}

const Modal: FC<ModalProps> = ({ isOpen, toggleModal, videoid }) => (
  <>
    {isOpen && (
      <>
        <div
          onClick={toggleModal}
          aria-hidden="true"
          className="fixed inset-0 z-[99999] bg-black opacity-50"
        ></div>
        <div
          role="dialog"
          aria-modal="true"
          className="fixed left-1/2 top-1/2 z-[9999999] flex w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 transform items-center justify-center md:w-11/12 lg:w-[55%]"
        >
          <div className="relative w-full p-4">
            <div className="relative rounded-lg bg-white shadow">
              <button
                onClick={toggleModal}
                aria-label="Close video modal"
                className="absolute right-2 top-2 rounded-full bg-black p-2 text-[#202020] hover:bg-gray-800"
              >
                ✕
              </button>
              {videoid && (
                <iframe
                  width="100%"
                  height="500"
                  src={`https://www.youtube.com/embed/${videoid}?autoplay=1`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              )}
            </div>
          </div>
        </div>
      </>
    )}
  </>
);

interface VideoListProps {
  videos?: Video[];
  activeTab: string;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}

const VideoList: FC<VideoListProps> = ({
  activeTab = "gettingStarted",
  videos = [],
  searchTerm,
  setSearchTerm,
}) => {
  const [currentItem, setCurrentItem] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleModal = useCallback((item?: Video) => {
    setCurrentItem(item?.videoid || null);
    setIsOpen((prev) => !prev);
  }, []);

  const filteredVideos = useMemo(() => {
    if (!searchTerm.trim().length) {
      return videosData[activeTab] || [];
    }
    const simplifiedVideosData = Object.values(videosData).flat();
    return simplifiedVideosData.filter(
      (video) =>
        video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.description.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [activeTab, searchTerm]);

  return (
    <>
      <div className="pb-4">
        <input
          type="search"
          placeholder="Search videos globally..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="z-20 w-full rounded-lg border border-[#CECECE] px-4 py-2 text-[#202020] placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#ffffff1a]"
        />
      </div>
      {filteredVideos.length === 0 ? (
        <div className="flex h-96 items-center justify-center">
          <p className="text-xl text-[#202020]">
            No videos found. Try another search.
          </p>
        </div>
      ) : (
        <div
          key={filteredVideos?.length}
          className="mx-auto grid max-w-screen-2xl grid-cols-1 gap-6 md:grid-cols-2 md:pt-[10px] xlg:grid-cols-2 xlg:gap-12"
        >
          {filteredVideos.map((item) => (
            <div key={item.videoid}>
              <div
                className="relative cursor-pointer"
                onClick={() => toggleModal(item)}
              >
                <Image
                  src={item.thumb}
                  alt={item.alt || `Kroolo video - ${item.title}`}
                  className="w-full"
                  width={700}
                  height={394}
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Image src={videoPlay} alt="Play video" className="w-[5rem]" />
                </div>
              </div>
              <div className="pt-1 md:pt-4">
                <div className="mb-1 flex items-center md:mb-[6px]">
                  <h4 className="text-[14px] font-medium text-[#202020] lg:text-[16px]">
                    {item.title}
                  </h4>
                </div>
                <p className="text-[14px] font-normal text-[#717171]">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal isOpen={isOpen} toggleModal={toggleModal} videoid={currentItem} />
    </>
  );
};

export default VideoList;
